/**
 * DemoSection — "take the ride-along" on the landing page (docs/DEMO.md).
 *
 * Renders the REAL app's /demo route inside device frames via same-origin
 * iframes. An iframe is its own viewport, so Tailwind's breakpoints fire
 * truthfully: the desktop frame shows the product's large-screen layout,
 * the phone frame shows the responsive mobile layout — both live, both
 * interactive, both the actual product code. No screenshots, no mockups.
 */

import { useEffect, useRef, useState } from 'react';

const DEVICES = {
  desktop: { width: 1280, height: 800, label: 'Desktop' },
  phone: { width: 390, height: 780, label: 'Phone' },
} as const;

type DeviceKind = keyof typeof DEVICES;

/**
 * Renders /demo at a device's native viewport, scaled to fit its container.
 * transform: scale keeps the iframe fully interactive.
 */
function ScaledFrame({ device }: { device: DeviceKind }) {
  const { width, height } = DEVICES[device];
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => {
      const available = el.clientWidth;
      setScale(Math.min(1, available / width));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [width]);

  return (
    <div ref={containerRef} className="w-full flex justify-center">
      <div
        className="relative"
        style={{ width: width * scale, height: height * scale }}
      >
        <iframe
          src="/demo?embed=1"
          title={`CopApp interactive demo — ${DEVICES[device].label.toLowerCase()} view`}
          loading="lazy"
          style={{
            width,
            height,
            transform: `scale(${scale})`,
            transformOrigin: '0 0',
            border: '0',
          }}
          className="bg-copapp-dark"
        />
      </div>
    </div>
  );
}

export function DemoSection() {
  const [device, setDevice] = useState<DeviceKind>('desktop');

  return (
    <section id="demo" className="py-16 border-t border-white/10">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Don&apos;t take our word for it — take the call
          </h2>
          <p className="text-xl text-white/70 max-w-3xl mx-auto">
            This is the actual product running right here — not a video, not screenshots.
            Work a scripted call, read your debrief and scores, see the dashboard. No sign-up.
          </p>
        </div>

        {/* Device toggle */}
        <div className="flex justify-center items-center gap-2 mb-6">
          {(Object.keys(DEVICES) as DeviceKind[]).map((kind) => (
            <button
              key={kind}
              onClick={() => setDevice(kind)}
              className={`px-5 py-2 rounded-lg text-sm font-medium transition-all ${
                device === kind ? 'bg-blue-600 text-white' : 'bg-white/10 text-white/70 hover:bg-white/20'
              }`}
            >
              {DEVICES[kind].label}
            </button>
          ))}
          <a
            href="/demo"
            className="ml-2 px-5 py-2 rounded-lg text-sm font-medium bg-white/5 text-white/60 hover:text-white hover:bg-white/15 border border-white/15 transition-all"
          >
            Open full screen ↗
          </a>
        </div>

        {/* Device frames — same page, two real viewports */}
        {device === 'desktop' ? (
          <div className="rounded-xl border border-white/15 bg-black/40 shadow-2xl shadow-blue-500/5 overflow-hidden">
            {/* Browser chrome */}
            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/10 bg-white/5">
              <span className="w-2.5 h-2.5 rounded-full bg-white/20" />
              <span className="w-2.5 h-2.5 rounded-full bg-white/20" />
              <span className="w-2.5 h-2.5 rounded-full bg-white/20" />
              <span className="mx-auto px-6 py-0.5 rounded bg-white/10 text-[11px] text-white/50 select-none">
                copapp.ai/demo
              </span>
            </div>
            <ScaledFrame device="desktop" />
          </div>
        ) : (
          <div className="flex justify-center">
            <div className="rounded-[2rem] border border-white/20 bg-black/60 p-2.5 shadow-2xl shadow-blue-500/5">
              {/* Phone bezel */}
              <div className="flex justify-center pb-1.5">
                <span className="w-16 h-1.5 rounded-full bg-white/15" />
              </div>
              <div className="rounded-[1.4rem] overflow-hidden">
                <ScaledFrame device="phone" />
              </div>
            </div>
          </div>
        )}

        <p className="text-center text-white/40 text-xs mt-5 max-w-2xl mx-auto">
          The demo call is scripted so we can hand it to you without a login. In the live app,
          every scenario is generated fresh and the people in it respond to exactly what you say
          and do — no two calls run the same twice.
        </p>
      </div>
    </section>
  );
}
